import type { Node, Edge } from '@xyflow/react';

/**
 * Canvas Image Export (doc 06 §9 — "Canvas PNG/SVG export... design docs and
 * pitch decks")
 *
 * The design doc suggests `html-to-image` for a pixel-perfect DOM
 * screenshot of the live canvas. That package isn't in `package.json`
 * (checked before writing this), and installing a new npm dependency wasn't
 * done as part of this pass. Instead, this builds a **schematic** SVG
 * directly from node/edge data — a simplified box-and-line diagram (node
 * type, label, color; straight connector lines) rather than a faithful
 * screenshot of the custom-styled React node components.
 *
 * This intentionally does not try to reproduce each node type's real
 * on-canvas appearance (portraits, badges, diamond shapes, etc.) — it's a
 * readable overview diagram, which is what the stated use case (design
 * docs, pitch decks) actually needs.
 *
 * Two outputs, both derived from the same SVG:
 * - `downloadSvg()` — the SVG file directly.
 * - `downloadPng()` — rasterizes that SVG via an offscreen `<canvas>` +
 *   `Image`, using only native browser APIs (`Canvas2D`, `Image`,
 *   `URL.createObjectURL`) — no image-conversion library needed either.
 */

const DEFAULT_NODE_WIDTH = 180;
const DEFAULT_NODE_HEIGHT = 60;
const PADDING = 40;

interface LayoutNode {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label: string;
    color: string;
}

function layoutNodes(nodes: Node[]): LayoutNode[] {
    return nodes.map((n) => ({
        id: n.id,
        x: n.position.x,
        y: n.position.y,
        width: n.width ?? n.measured?.width ?? DEFAULT_NODE_WIDTH,
        height: n.height ?? n.measured?.height ?? DEFAULT_NODE_HEIGHT,
        label: nodeLabel(n),
        color: nodeColor(n),
    }));
}

function nodeLabel(n: Node): string {
    const data = (n.data ?? {}) as Record<string, unknown>;
    const raw =
        (data.display_name as string) ||
        (data.label as string) ||
        (data.name as string) ||
        (typeof data.text === 'string' ? data.text : '') ||
        n.type ||
        'Node';
    return raw.length > 40 ? raw.slice(0, 37) + '…' : raw;
}

function nodeColor(n: Node): string {
    const style = n.style as Record<string, unknown> | undefined;
    return (style?.backgroundColor as string) || (n.data?.color as string) || '#6c757d';
}

function escapeXml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Build a standalone SVG string of the current graph. Coordinates are
 * shifted so the bounding box starts at `PADDING, PADDING`.
 */
export function buildGraphSvg(nodes: Node[], edges: Edge[]): string {
    const layout = layoutNodes(nodes);

    if (layout.length === 0) {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><text x="20" y="100" font-family="sans-serif" fill="#888">Empty graph</text></svg>`;
    }

    const minX = Math.min(...layout.map((n) => n.x));
    const minY = Math.min(...layout.map((n) => n.y));
    const maxX = Math.max(...layout.map((n) => n.x + n.width));
    const maxY = Math.max(...layout.map((n) => n.y + n.height));

    const offsetX = PADDING - minX;
    const offsetY = PADDING - minY;
    const width = maxX - minX + PADDING * 2;
    const height = maxY - minY + PADDING * 2;

    const byId = new Map(layout.map((n) => [n.id, n]));

    const edgeLines = edges
        .map((e) => {
            const source = byId.get(e.source);
            const target = byId.get(e.target);
            if (!source || !target) return '';

            const x1 = source.x + source.width + offsetX;
            const y1 = source.y + source.height / 2 + offsetY;
            const x2 = target.x + offsetX;
            const y2 = target.y + target.height / 2 + offsetY;
            const midX = (x1 + x2) / 2;

            const label = typeof e.label === 'string' && e.label
                ? `<text x="${midX}" y="${(y1 + y2) / 2 - 6}" font-family="sans-serif" font-size="11" fill="#555" text-anchor="middle">${escapeXml(e.label)}</text>`
                : '';

            return `<path d="M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}" fill="none" stroke="#999" stroke-width="1.5" marker-end="url(#arrow)" />${label}`;
        })
        .join('\n');

    const nodeShapes = layout
        .map((n) => {
            const x = n.x + offsetX;
            const y = n.y + offsetY;
            return `
                <g>
                    <rect x="${x}" y="${y}" width="${n.width}" height="${n.height}" rx="6" fill="white" stroke="${escapeXml(n.color)}" stroke-width="2" />
                    <rect x="${x}" y="${y}" width="${n.width}" height="6" fill="${escapeXml(n.color)}" />
                    <text x="${x + n.width / 2}" y="${y + n.height / 2 + 5}" font-family="sans-serif" font-size="12" fill="#222" text-anchor="middle">${escapeXml(n.label)}</text>
                </g>`;
        })
        .join('\n');

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
            <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill="#999" />
            </marker>
        </defs>
        <rect x="0" y="0" width="${width}" height="${height}" fill="#f8f9fa" />
        ${edgeLines}
        ${nodeShapes}
    </svg>`;
}

function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function downloadGraphSvg(nodes: Node[], edges: Edge[], filename = 'graph.svg'): void {
    const svg = buildGraphSvg(nodes, edges);
    triggerDownload(new Blob([svg], { type: 'image/svg+xml' }), filename);
}

/**
 * Rasterize the same SVG to PNG using only native browser APIs: load the
 * SVG as a data URI into an `Image`, draw it onto an offscreen `<canvas>`,
 * then export via `canvas.toBlob`. No image-conversion library needed.
 */
export function downloadGraphPng(nodes: Node[], edges: Edge[], filename = 'graph.png'): Promise<void> {
    const svg = buildGraphSvg(nodes, edges);
    const svgDataUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas 2D context unavailable'));
                return;
            }
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('PNG rasterization failed'));
                    return;
                }
                triggerDownload(blob, filename);
                resolve();
            }, 'image/png');
        };
        img.onerror = () => reject(new Error('Failed to load SVG for rasterization'));
        img.src = svgDataUri;
    });
}
